import { createListenerMiddleware } from '@reduxjs/toolkit';
import { logout } from 'modules/auth/actions';
import formActions from 'modules/form/actions';
import * as actions from 'modules/selfServices/actions';

import { PAGE_SIZE } from 'modules/chat/constants';

import addHtmlAttribute from 'utils/appUtils';
import type rootReducer from 'App/rootReducer';
import { CustomerInfoForm } from 'types/CustomerInfoForm';
import FormKeys, { CONCILIATABLE_FORM_KEYS } from 'modules/form/types/FormKeys';

import chatApi from 'modules/chat/service';
import Conciliate from 'modules/form/conciliateUtils';
import { AppStartListening } from 'App/ListenerMiddleware';
import selfServicesApi from 'modules/selfServices/service';

import { newIncomingMessage } from 'modules/sockets/actions';

import { getAppOrigin, getAppTheme } from 'modules/app/selectors';
import { getTotalElapsedTime } from 'modules/elapsedTime/selectors';
import { getLoginData, getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getFormValuesByKey } from 'modules/form/selectors';

import finalizeApi from './finalizeService';
import { FINALIZE_FIXED_CACHE_KEY } from './constants';

const listenerMiddleware = createListenerMiddleware();

const startAppListening = listenerMiddleware.startListening as AppStartListening;

startAppListening({
  actionCreator: actions.finalizeSelfService,
  effect: async ({ payload }, { dispatch, getState, subscribe, unsubscribe }) => {
    unsubscribe();

    try {
      const totalElapsedTime = getTotalElapsedTime(getState());
      const selfServiceId = getSelectedSelfServiceId(getState());

      const { error } = await dispatch(finalizeApi.endpoints.submitFinalization.initiate({
        id: selfServiceId,
        elapsedTime: totalElapsedTime + payload.time,
      }, { fixedCacheKey: FINALIZE_FIXED_CACHE_KEY }));

      if (!error && payload.shouldReset) {
        dispatch(logout({ doorTimeout: true }));
      }
    } finally {
      subscribe();
    }
  },
});

// Re-fetch the final instructions after the chat is read (for the first time) to hide the indicator
startAppListening({
  matcher: chatApi.endpoints.readMessages.matchFulfilled,
  effect: (_, { dispatch, getState }) => {
    const selfServiceId = getSelectedSelfServiceId(getState());
    const finalInstructions = selfServicesApi.endpoints.getFinalInstructions.select({ selfServiceId })(getState());
    if (finalInstructions.data?.unreadCount > 0) {
      dispatch(selfServicesApi.endpoints.getFinalInstructions.initiate({ selfServiceId }, { forceRefetch: true }));
    }
  },
});

// Fetch the first batch of messages when we receive a new message, to show the updated badge indicator
startAppListening({
  matcher: newIncomingMessage.match,
  effect: (_, { dispatch, getState }) => {
    const selfServiceId = getSelectedSelfServiceId(getState());
    dispatch(
      chatApi.endpoints.getMessages.initiate(
        { selfServiceId, page: 0, size: PAGE_SIZE },
      ),
    );
  },
});

startAppListening({
  matcher: selfServicesApi.endpoints.getSelfServiceById.matchFulfilled,
  effect: (action, { dispatch, getState }) => {
    const { payload: selfService } = action;
    const state = getState() as ReturnType<typeof rootReducer>;

    const key = getAppOrigin(state);
    const theme = getAppTheme(state);
    addHtmlAttribute('data-origin', key);
    addHtmlAttribute('data-theme', theme);

    const loginData = getLoginData(state);

    // Conciliate received data and cached data
    const newCache = CONCILIATABLE_FORM_KEYS.reduce((acc, formKey) => {
      const fn = Conciliate[`${formKey}Conciliate`];
      if (typeof fn === 'function') {
        acc.push({
          key: formKey,
          values: fn({ selfService, loginData }, getFormValuesByKey(state, formKey)),
        });
      }
      return acc;
    }, []);

    dispatch(formActions.upsertFormMany(newCache));
    dispatch(
      selfServicesApi.endpoints.getPaymentMethods.initiate({ selfServiceId: selfService.id }, { forceRefetch: true }),
    );
  },
});

startAppListening({
  matcher: selfServicesApi.endpoints.updateCustomerInfo.matchFulfilled,
  effect: (action, { dispatch }) => {
    const { permanent } = action.meta.arg.originalArgs.params;

    dispatch(
      formActions.upsertForm({
        key: FormKeys.CUSTOMER_INFO,
        values: {
          ...action.payload.customerInfo,
          permanent: permanent.toString(),
        } as CustomerInfoForm,
      }),
    );
  },
});

export default listenerMiddleware;
